import { useState, useEffect, useContext } from "react";
import { SocketContext } from "../../context/socket";
import CustomLeaderboardPreview from "../../routes/viewers/CustomLeaderboardPreview";

export default function AllPlayersPreview({ params, currentPage = 0, onPreviewDataUpdate }) {
   const socket = useContext(SocketContext);
   const [activeData, setActiveData] = useState(null);
   const [show, setShow] = useState(true);

   useEffect(() => {
      if (params) {
         try {
            const parsed = typeof params === 'string' ? JSON.parse(params) : params;
            const { roundId, archetype, columns, rows, isAlphabetical, playerIds } = parsed;

            if (isAlphabetical && playerIds) {
               // Round 0 (Alphabetical) - get all players, sort alphabetically, add rank and 0 0 0 for W L D
               socket.emit("getPlayers", (allPlayers) => {
                  if (allPlayers && allPlayers.length > 0) {
                     // Create a map of player IDs to players for quick lookup
                     const playerMap = new Map();
                     allPlayers.forEach(player => {
                        const id = player.id || player.PlayerId;
                        if (id) {
                           playerMap.set(id, player);
                        }
                     });

                     // Get players in the order specified by playerIds (which are already sorted alphabetically)
                     const sortedPlayers = playerIds
                        .map(id => playerMap.get(id))
                        .filter(Boolean);

                     // Transform player data into the format expected by CustomLeaderboardPreview
                     const transformedRows = sortedPlayers.map((player, index) => ({
                        Rank: index + 1, // Rank starts at 1
                        PlayerName: player.PlayerName || player.PlayerNameLastFirst || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                        MatchWins: 0, // Set to 0 for alphabetical
                        MatchLosses: 0, // Set to 0 for alphabetical
                        MatchDraws: 0, // Set to 0 for alphabetical
                        FormatDecklistName: archetype === 1 ? (player.FormatDecklistName || player.Archetype || "") : "",
                        TEAM: archetype === 2 ? (player.TEAM || "") : "",
                        STRIXHAVEN_SCHOOL: archetype === 3 ? (player.STRIXHAVEN_SCHOOL || "") : "",
                        Country: player.Country || "",
                        CountryFlag: player.CountryFlag || "",
                        HOF: player.HOF || 0,
                     }));

                     setActiveData({
                        Title: "Players",
                        // Don't set Round or StandingsId for Round 0 (Alphabetical) - no subtitle
                        rows: transformedRows,
                        Columns: columns || 1,
                        Rows: rows || 8,
                        Archetype: archetype || 0,
                     });
                  }
               });
            } else {
               // Regular round - load all player data for the selected round
               socket.emit("getCustomLeaderboardAllPlayers", roundId, (allPlayersData) => {
                  if (allPlayersData && allPlayersData.length > 0) {
                     // Get round information
                     socket.emit("getTournamentRounds", (rounds) => {
                        let round;
                        let roundName;
                        
                        if (roundId === 0) {
                           // Most Recent - get the round name from the first row of standings data
                           const firstRow = allPlayersData[0];
                           roundName = firstRow?.Round || firstRow?.round || "Most Recent";
                           
                           // Transform player data into the format expected by CustomLeaderboardPreview
                           const transformedRows = allPlayersData.map((player, index) => ({
                              Rank: player.Rank || player.rank || index + 1,
                              PlayerName: player.PlayerName || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                              MatchWins: player.MatchWins || player.MatchWins || 0,
                              MatchLosses: player.MatchLosses || player.MatchLosses || 0,
                              MatchDraws: player.MatchDraws || player.MatchDraws || 0,
                              FormatDecklistName: archetype === 1 ? (player.FormatDecklistName || player.Archetype || "") : "",
                              TEAM: archetype === 2 ? (player.TEAM || "") : "",
                              STRIXHAVEN_SCHOOL: archetype === 3 ? (player.STRIXHAVEN_SCHOOL || "") : "",
                              Country: player.Country || "",
                              CountryFlag: player.CountryFlag || "",
                              HOF: player.HOF || 0,
                           }));

                     setActiveData({
                        Title: "PLAYER STANDINGS",
                        Round: roundName,
                        StandingsId: roundId, // Set to show Round
                        rows: transformedRows,
                        Columns: columns || 1,
                        Rows: rows || 8,
                        Archetype: archetype || 0,
                        showRoundInOrange: true, // Flag to show round in orange
                     });
                        } else {
                           // Specific round selected
                           round = rounds?.find(r => r.ID === roundId) || { Name: "Round " + roundId };
                           
                           // Transform player data into the format expected by CustomLeaderboardPreview
                           const transformedRows = allPlayersData.map((player, index) => ({
                              Rank: player.Rank || player.rank || index + 1,
                              PlayerName: player.PlayerName || `${player.FirstName || ''} ${player.LastName || ''}`.trim(),
                              MatchWins: player.MatchWins || player.MatchWins || 0,
                              MatchLosses: player.MatchLosses || player.MatchLosses || 0,
                              MatchDraws: player.MatchDraws || player.MatchDraws || 0,
                              FormatDecklistName: archetype === 1 ? (player.FormatDecklistName || player.Archetype || "") : "",
                              TEAM: archetype === 2 ? (player.TEAM || "") : "",
                              STRIXHAVEN_SCHOOL: archetype === 3 ? (player.STRIXHAVEN_SCHOOL || "") : "",
                              Country: player.Country || "",
                              CountryFlag: player.CountryFlag || "",
                              HOF: player.HOF || 0,
                           }));

                           setActiveData({
                              Title: "PLAYER STANDINGS",
                              Round: round.Name,
                              StandingsId: roundId, // Set to show Round
                              rows: transformedRows,
                              Columns: columns || 1,
                              Rows: rows || 8,
                              Archetype: archetype || 0,
                              showRoundInOrange: true, // Flag to show round in orange
                           });
                        }
                     });
                  }
               });
            }
         } catch (error) {
            console.error("Error parsing all players params:", error);
         }
      }
   }, [params]);

   // Expose activeData to parent via callback
   useEffect(() => {
      if (onPreviewDataUpdate && activeData) {
         onPreviewDataUpdate(activeData);
      }
   }, [activeData, onPreviewDataUpdate]);

   if (!activeData) {
      return null;
   }

   return (
      <CustomLeaderboardPreview
         currentPage={currentPage}
         activeData={activeData}
         show={show}
         preview={true}
      />
   );
}
